import { isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNullOrUndefined, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { create, getAll, getById, getByIdQuery, getBySingleQuery, remove, update, updateField } from '../utils/dbUtils';
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from "firebase/firestore";
import { asTimeZone } from "../utils/dateFormatter";
import { db } from "../firebase";
import moment from "moment-timezone";

class ShippingMethod {
    static collectionName = 'shippingMethods';

    name = ""
    enabled = false;
    taxStatus = "TAXABLE";
    cost = null; // formula; either amount or amount * [qty] or amount + (amount * [qty]) i.e. 20 for flat rate, 20 * [qty] for 20 per item, 20 + (2 * [qty]) for base amount plus per item amount
    shippingClassCosts = []; // array of { shippingClassId, cost }
    calculationType = "ORDER"; // Enum ShippingCalculationTypes

    constructor(shippingMethod) {
        if (isObjectNotEmpty(shippingMethod)) {
            if (isStringNotEmpty(shippingMethod.name)) { this.name = shippingMethod.name; }
            if (isBooleanTrue(shippingMethod.enabled)) { this.enabled = true; }
            if (isStringNotEmpty(shippingMethod.taxStatus)) { this.taxStatus = shippingMethod.taxStatus; }
            if (isNotNullOrUndefined(shippingMethod.cost)) { this.cost = shippingMethod.cost; }
            if (isListNotEmpty(shippingMethod.shippingClassCosts)) { this.shippingClassCosts = shippingMethod.shippingClassCosts; }
            if (isStringNotEmpty(shippingMethod.calculationType)) { this.calculationType = shippingMethod.calculationType; }
        }
    }

    toDto = () => {
        return {
            name: this.name,
            enabled: this.enabled,
            taxStatus: this.taxStatus,
            cost: this.cost,
            shippingClassCosts: this.shippingClassCosts,
            calculationType: this.calculationType,
        };
    }

    isValid = () => {
        if (isStringEmpty(this.name)) {
            throw new Error('Shipping method name is required.');
        }
        if (isNullOrUndefined(this.enabled)) {
            throw new Error('Enabled is required.');
        }
        if (isStringEmpty(this.taxStatus)) {
            throw new Error('Tax status is required.');
        }
        if (isStringEmpty(this.calculationType)) {
            throw new Error('Calculation type is required.');
        }

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            return await create(ShippingMethod.collectionName, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(ShippingMethod.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedShippingMethod) => {
        const updatedObj = new ShippingMethod(updatedShippingMethod);
        if (updatedObj.isValid() && id === updatedShippingMethod.id) {
            return await update(ShippingMethod.collectionName, id, updatedObj.toDto());
        } else {
            throw new Error('id and id field of the updated shipping method must match.')
        }
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(ShippingMethod.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(ShippingMethod.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(ShippingMethod.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(ShippingMethod.collectionName, fieldName, fieldValue, comparer);
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(ShippingMethod.collectionName, ids, 'in');
    }

    static getCount = async (queryParameters) => {
        const { start, end } = queryParameters;

        let queryConstraints = [];

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = query(collection(db, ShippingMethod.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        //console.log(snapShot.data());

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
        const { start, end } = queryParameters;

        let queryConstraints = [];

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, ShippingMethod.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, ShippingMethod.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, ShippingMethod.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;
            let totalPages = 0;

            let totalCount = await this.getCount(queryParameters);
            totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await this.getPaginated(queryParameters, 10000, null, next, 'createdAt');

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }
}

export default ShippingMethod;